// src/modules/smelter/pages/CreateDispatch.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchSmelters, createDispatch } from '../api';
import { fetchBatches } from '../../goldInventory/api';
import {
  Form, Select, DatePicker, Button, Typography, Card, Row, Col, message, Space, Descriptions, Table,
} from 'antd';
import { ArrowLeftOutlined, FileTextOutlined } from '@ant-design/icons';
import TransitMemoModal from '../components/TransitMemoModal';
import { usePermissions } from '../../../hooks/usePermissions';
import dayjs from 'dayjs';

const { Title } = Typography;
const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

export default function CreateDispatch() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { canViewCreateModule } = usePermissions();
  const [form] = Form.useForm();
  const [smelters, setSmelters] = useState([]);
  const [holdingBatches, setHoldingBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [memoOpen, setMemoOpen] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    Promise.all([
      fetchSmelters({ active: true }),
      fetchBatches({ status: 'HOLDING', limit: 100 }),
    ])
      .then(([smelterRes, batchRes]) => {
        setSmelters(smelterRes.data || []);
        setHoldingBatches(batchRes.data || []);

        const batchId = searchParams.get('batchId');
        if (batchId) {
          const match = (batchRes.data || []).find((b) => b._id === batchId);
          if (match) {
            form.setFieldsValue({ inventoryBatchId: batchId });
            setSelectedBatch(match);
          }
        }
      })
      .catch(() => messageApi.error('Failed to load form data'));
  }, [form, searchParams]);

  const handleBatchChange = (id) => {
    const b = holdingBatches.find((x) => x._id === id);
    setSelectedBatch(b || null);
  };

  // The transit memo travels with the metal, so it is raised from the same screen
  // and pre-filled with whatever has already been picked here.
  const memoPrefill = () => {
    const smelterId = form.getFieldValue('smelterId');
    const b = selectedBatch;
    if (!b) return { smelterId };
    const items = (b.batchType === 'ITEMIZED' && (b.items || []).length)
      ? b.items.map((it) => ({
        description: it.label || `Melted ${(b.metalType || 'GOLD') === 'SILVER' ? 'Silver' : 'Gold'} Jwellery`,
        itemCount: 1,
        grossWeight: it.grossWeight || 0,
        remarks: 'Company Stock',
      }))
      : [{
        description: `Melted ${(b.metalType || 'GOLD') === 'SILVER' ? 'Silver' : 'Gold'} Jwellery`,
        itemCount: b.caseCount || 1,
        grossWeight: b.totalGrossWeight || 0,
        remarks: 'Company Stock',
      }];
    return {
      smelterId,
      metalType: b.metalType || 'GOLD',
      inventoryBatchId: b._id,
      batchNumber: b.batchId,
      items,
      totalWeight: b.totalGrossWeight || 0,
    };
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const res = await createDispatch({
        inventoryBatchId: values.inventoryBatchId,
        smelterId: values.smelterId,
        dispatchDate: values.dispatchDate.toISOString(),
      });
      messageApi.success('Dispatch created');
      navigate(`/smelters/dispatches/${res.data._id}`);
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to create dispatch');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 700, margin: '0 auto' }}>
      {contextHolder}
      <Row align="middle" style={{ marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/smelters/dispatches')}>Back</Button>
          <Title level={4} style={{ margin: 0 }}>New Smelter Dispatch</Title>
          <Button icon={<FileTextOutlined />} onClick={() => setMemoOpen(true)}
            disabled={!canViewCreateModule('smelter')}>
            Gold Transit Memo
          </Button>
        </Space>
      </Row>

      <Card>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item label="Inventory Batch" name="inventoryBatchId" rules={[{ required: true }]}>
            <Select
              placeholder="Select HOLDING batch…"
              showSearch
              filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
              onChange={handleBatchChange}
              options={holdingBatches.map((b) => ({
                value: b._id,
                label: `${b.batchId} — ${b.caseCount} cases, ${b.totalNetWeight?.toFixed(3)}g net (${b.batchType || 'SINGLE'})`,
              }))}
            />
          </Form.Item>

          {selectedBatch && (
            <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Batch ID">{selectedBatch.batchId}</Descriptions.Item>
                <Descriptions.Item label="Cases">{selectedBatch.caseCount}</Descriptions.Item>
                <Descriptions.Item label="Type">{selectedBatch.batchType || 'SINGLE'}</Descriptions.Item>
                <Descriptions.Item label="Net Weight">{selectedBatch.totalNetWeight?.toFixed(3)}g</Descriptions.Item>
                <Descriptions.Item label="Gross Weight">{selectedBatch.totalGrossWeight?.toFixed(3)}g</Descriptions.Item>
                <Descriptions.Item label="Purchase Cost">{fmt(selectedBatch.totalPurchaseCost)}</Descriptions.Item>
              </Descriptions>
              {selectedBatch.batchType === 'ITEMIZED' && (selectedBatch.items || []).length > 0 && (
                <Table
                  size="small"
                  style={{ marginTop: 12 }}
                  dataSource={selectedBatch.items}
                  rowKey={(r, idx) => idx}
                  pagination={false}
                  columns={[
                    { title: 'Label', dataIndex: 'label' },
                    { title: 'Gross Wt (g)', dataIndex: 'grossWeight', render: (v) => v?.toFixed(3) },
                    { title: 'Purity %', dataIndex: 'purityPercent', render: (v) => v?.toFixed(2) },
                    { title: 'Price (₹)', dataIndex: 'priceAmount', render: fmt },
                  ]}
                />
              )}
            </Card>
          )}

          <Form.Item label="Smelter" name="smelterId" rules={[{ required: true }]}>
            <Select
              placeholder="Select smelter…"
              showSearch
              filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
              options={smelters.map((s) => ({ value: s._id, label: `${s.name} — ${s.contactPerson || ''}` }))}
            />
          </Form.Item>

          <Form.Item label="Dispatch Date" name="dispatchDate"
            rules={[{ required: true }]} initialValue={dayjs()}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>

          <Row justify="end">
            <Space>
              <Button onClick={() => navigate(-1)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitting} disabled={!canViewCreateModule('smelter')}>Create Dispatch</Button>
            </Space>
          </Row>
        </Form>
      </Card>

      <TransitMemoModal
        open={memoOpen}
        onClose={() => setMemoOpen(false)}
        prefill={memoOpen ? memoPrefill() : {}}
      />
    </div>
  );
}
